import { useMemo } from "react";
import { createGlobalStyle, ThemeProvider, useTheme } from "styled-components";
import { getDefaultButtons, TButtons } from "../button/Button";
import { getDefaultTableDarkTheme, getDefaultTableLightTheme } from "../table/Table";
import { getDefaultTabsDarkTheme, getDefaultTabsLightTheme } from "../tabs/Tabs";
import { getDefaultToggleDarkTheme, getDefaultToggleLightTheme } from "../toggle/Toggle";

export type TTypos = typeof getDefaultTypos;
export type TTypoVariant = keyof TTypos;
export type TDefaultLightThemeColors = typeof DefaultLightThemeColors;

export interface ITheme {
	name: string;
	typo?: TTypos;
	buttons?: TButtons;
	table?: typeof getDefaultTableLightTheme;
	tabs?: typeof getDefaultTabsLightTheme;
	toggle?: typeof getDefaultToggleLightTheme;
	colors: TDefaultLightThemeColors;
}

export type RecursiveCSS = any;

interface GlobalThemeProps {
	theme?: ITheme;
	children?: any;
}

export const DefaultLightThemeColors = {
	// document
	backgroundPrimary: "#e4e7ea",

	// panels
	panelBackgroundPrimary: "#FFFFFF",
	panelDividerBackgroundColor: "linear-gradient(0deg, rgba(255, 255, 255, 0.05) 50%, rgba(0, 0, 0, 0.1) 50%)",
	panelHeadBackgroundColor: "rgba(0, 0, 0, 0.05)",

	// input
	inputBackgroundPrimary: "#FFFFFF",
	inputBorderPrimary: "rgba(43, 33, 79, 0.3)",
	inputBorderSecondary: "#101A24",

	// text
	textPrimary: "#14171F",
	textSecondary: "#848799",
	textWarning: "#e91e63",

	// Buttons
	buttonBackgroundPrimary: "#101A24",
	buttonBackgroundSecondary: "#848799",
	buttonTextPrimary: "#FFFFFF",
	buttonBackgroundPrimaryHover: "#1d2228",
	buttonTextSecondary: "#14171F",

	// tabs
	tabsHoverColor: "rgb(249, 251, 255)",

	chartPallete: ["#7400B8", "#6930C3", "#5E60CE", "#5390D9", "#4EA8DE", "#48BFE3", "#56CFE1", "#64DFDF", "#72EFDD", "#80FFDB"],

	chartForeground: "rgba(103, 123, 148, 1)",
	chartForegroundHover: "#88a4c5",
	chartLightForeground: "rgba(31, 194, 194, 1)",
	chartBackground: "rgba(242, 242, 242, 1)",

	success: "#56bb56",
	fail: "#bb5656",

	successText: "#48742f",
	failText: "#742f2f",

	divider: "#e2e2e2",

	iconLight: "#b1b1b1",
};

export const DefaultDarkThemeColors: TDefaultLightThemeColors = {
	backgroundPrimary: "#19141f",

	inputBackgroundPrimary: "rgba(0,0,0,.1)",
	inputBorderPrimary: "rgba(255, 255, 255, 0.1)",
	inputBorderSecondary: "#0b6df5",

	panelBackgroundPrimary: "#231d2b",
	panelHeadBackgroundColor: "rgba(0, 0, 0, 0.1)",
	panelDividerBackgroundColor: "linear-gradient(0deg, rgba(255, 255, 255, 0.05) 50%, rgba(0, 0, 0, 0.8) 50%)",

	textPrimary: "#FFFFFF",
	textSecondary: "blue",
	textWarning: "#e91e63",

	buttonBackgroundPrimary: "#0b6df5",
	buttonBackgroundSecondary: "orange",
	buttonTextPrimary: "yellow",
	buttonBackgroundPrimaryHover: "purple",
	buttonTextSecondary: "pink",

	chartPallete: ["#7400B8", "#6930C3", "#5E60CE", "#5390D9", "#4EA8DE", "#48BFE3", "#56CFE1", "#64DFDF", "#72EFDD", "#80FFDB"],

	tabsHoverColor: "rgba(0,0,0,.1)",

	chartForeground: "rgba(103, 123, 148, 1)",
	chartForegroundHover: "#88a4c5",
	chartLightForeground: "rgba(31, 194, 194, 1)",
	chartBackground: "rgba(242, 242, 242, 1)",

	success: "#56bb56",
	fail: "#bb5656",

	successText: "#48742f",
	failText: "#742f2f",

	divider: "#e2e2e2",

	iconLight: "#7e96b3",
};

export const getDefaultTypos = {
	head: {
		fontSize: "1.7rem",
		fontWeight: 600,
		color: "textPrimary",
		// marginBottom:"10px",
		display: "inline",
	},
	headMedium: {
		fontSize: "1.15rem",
		fontWeight: 500,
		color: "textPrimary",
		// marginBottom:"10px",
		display: "inline",
	},
	text: {
		fontSize: "1rem",
		fontWeight: 400,
		color: "textPrimary",
		display: "inline",
	},
	medium: {
		fontSize: ".88rem",
		fontWeight: 400,
		color: "textPrimary",
		display: "inline",
	},
	caption: {
		fontSize: ".75rem",
		fontWeight: 400,
		color: "textPrimary",
		display: "inline",
		opacity: 0.6,
	},
	small: {
		fontSize: ".6rem",
		fontWeight: 400,
		color: "textPrimary",
		display: "inline",
	},
	tiny: {
		fontSize: ".8rem",
		fontWeight: 400,
		color: "textPrimary",
		display: "inline",
	},
};

export const isLightColor = (hex: string): boolean => {
	hex = hex.replace(/^#/, "");

	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

	return brightness > 185;
};

export const getDefaultLightTheme = (): ITheme => {
	return {
		name: "light",
		colors: JSON.parse(JSON.stringify(DefaultLightThemeColors)),
		typo: JSON.parse(JSON.stringify(getDefaultTypos)),
		buttons: JSON.parse(JSON.stringify(getDefaultButtons)),
		table: JSON.parse(JSON.stringify(getDefaultTableLightTheme)),
		tabs: JSON.parse(JSON.stringify(getDefaultTabsLightTheme)),
		toggle: JSON.parse(JSON.stringify(getDefaultToggleLightTheme)),
	};
};

export const getDefaultDarkTheme = (): ITheme => {
	return {
		name: "dark",
		colors: JSON.parse(JSON.stringify(DefaultDarkThemeColors)),
		typo: JSON.parse(JSON.stringify(getDefaultTypos)),
		buttons: JSON.parse(JSON.stringify(getDefaultButtons)),
		table: JSON.parse(JSON.stringify(getDefaultTableDarkTheme)),
		tabs: JSON.parse(JSON.stringify(getDefaultTabsDarkTheme)),
		toggle: JSON.parse(JSON.stringify(getDefaultToggleDarkTheme)),
	};
};

// NOTE: @font-face is intentionally NOT here. It lives in a static CSS file (frontend
// src/fonts.css, imported once in main.tsx) so the font <style> is never tied to a React
// component lifecycle — a styled-components global style gets torn down when its host
// unmounts (e.g. React.StrictMode's dev mount→unmount→remount), which cancels in-flight
// font requests (NS_BINDING_ABORTED). BaseStyle below only *references* the family.
const BaseStyle = createGlobalStyle`
	body{
		font-family: 'Euclid Circular B', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans',
        'Helvetica Neue', sans-serif;
    	-webkit-font-smoothing: antialiased;
    	-moz-osx-font-smoothing: grayscale;
		margin: 0;
		padding: 0;
		color:${({ theme }: { theme: ITheme }) => theme.colors.textPrimary};
		background-color:${({ theme }: { theme: ITheme }) => theme.colors.backgroundPrimary};
		
	}

	*{
		box-sizing: border-box;
	}
	::-webkit-scrollbar {
		width: 6px;
		height: 6px;
	}
	
	::-webkit-scrollbar-track {
		background-color:rgba(0,0,0,.2);
		border-radius: 3px;
	}
	
	::-webkit-scrollbar-thumb {
		background-color:rgba(0,0,0,.3);
		border-radius: 3px;
	}

	::-webkit-scrollbar-thumb:hover {
		background-color:rgba(0,0,0,1);
	}
`;

const reparseColors = (colors: TDefaultLightThemeColors) => {
	for (let i in colors) {
		const value = (colors as any)[i];
		if ((colors as any)[value]) (colors as any)[i] = (colors as any)[value];
	}
};

const parseThemeColors = (theme: ITheme, colors: TDefaultLightThemeColors): ITheme => {
	// recursively parse theme
	for (let i in theme) {
		if (i === "colors") continue;

		if (typeof (theme as any)[i] === "object") {
			(theme as any)[i] = parseThemeColors((theme as any)[i], colors);
			continue;
		}

		if (typeof (theme as any)[i] === "string") {
			const value = (theme as any)[i];
			if ((colors as any)[value]) {
				(theme as any)[i] = (colors as any)[value];
			} else {
				// find color in value
				for (let j in colors) {
					if (value.indexOf(j) > -1) {
						(theme as any)[i] = value.replace(j, (colors as any)[j]);
					}
				}
			}
		}
	}
	return theme;
};

const parseTheme = (theme: ITheme): ITheme => {
	// reparse colors
	reparseColors(theme.colors);
	// recursively parse theme
	parseThemeColors(theme, theme.colors);
	return theme;
};

export const GlobalTheme = ({ theme, children }: GlobalThemeProps) => {
	// parseTheme mutates and returns its argument; memoize so it runs once per theme instead
	// of rebuilding/re-parsing a fresh object on every render (which churned the ThemeProvider
	// and the themed global styles).
	const parsedTheme = useMemo(() => parseTheme(theme ?? getDefaultLightTheme()), [theme]);
	return (
		<ThemeProvider theme={parsedTheme}>
			<BaseStyle theme={parsedTheme} />
			{children}
		</ThemeProvider>
	);
};

export const UITheme = useTheme;
